// utils/searchStorage.ts
// Utility functions for managing search history and analytics in localStorage

export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  type: 'user' | 'suggestion';
}

export interface SearchAnalytics {
  query: string;
  count: number;
  lastSearched: number;
}

const SEARCH_HISTORY_KEY = 'loci_search_history';
const SEARCH_ANALYTICS_KEY = 'loci_search_analytics';
const MAX_HISTORY_ITEMS = 20;
const MAX_ANALYTICS_ITEMS = 100;

/**
 * Get search history from localStorage
 */
export const getSearchHistory = (): SearchHistoryItem[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(SEARCH_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Error reading search history:', error);
    return [];
  }
};

/**
 * Add a search query to history
 */
export const addToSearchHistory = (query: string, type: 'user' | 'suggestion' = 'user'): void => {
  if (typeof window === 'undefined' || !query.trim()) return;

  try {
    const history = getSearchHistory();
    const newItem: SearchHistoryItem = {
      query: query.trim(),
      timestamp: Date.now(),
      type,
    };

    // Remove any existing entry for this query
    const filteredHistory = history.filter((item) => item.query !== newItem.query);

    // Add to beginning and limit size
    const updatedHistory = [newItem, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);

    localStorage.setItem(SEARCH_HISTORY_KEY, JSON.stringify(updatedHistory));
  } catch (error) {
    console.warn('Error saving search history:', error);
  }
};

/**
 * Clear search history
 */
export const clearSearchHistory = (): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(SEARCH_HISTORY_KEY);
  } catch (error) {
    console.warn('Error clearing search history:', error);
  }
};

/**
 * Get search analytics from localStorage
 */
export const getSearchAnalytics = (): SearchAnalytics[] => {
  if (typeof window === 'undefined') return [];

  try {
    const stored = localStorage.getItem(SEARCH_ANALYTICS_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Error reading search analytics:', error);
    return [];
  }
};

/**
 * Track a search query for analytics
 */
export const trackSearchQuery = (query: string): void => {
  if (typeof window === 'undefined' || !query.trim()) return;

  try {
    const analytics = getSearchAnalytics();
    const trimmedQuery = query.trim().toLowerCase();

    // Find existing analytics for this query
    const existingIndex = analytics.findIndex((item) => item.query === trimmedQuery);

    if (existingIndex >= 0) {
      // Update existing entry
      analytics[existingIndex].count++;
      analytics[existingIndex].lastSearched = Date.now();
    } else {
      // Add new entry
      analytics.push({
        query: trimmedQuery,
        count: 1,
        lastSearched: Date.now(),
      });
    }

    // Sort by count (descending) and limit size
    const sortedAnalytics = analytics
      .sort((a, b) => b.count - a.count)
      .slice(0, MAX_ANALYTICS_ITEMS);

    localStorage.setItem(SEARCH_ANALYTICS_KEY, JSON.stringify(sortedAnalytics));
  } catch (error) {
    console.warn('Error saving search analytics:', error);
  }
};

/**
 * Get popular search queries
 */
export const getPopularSearches = (limit: number = 5): string[] => {
  const analytics = getSearchAnalytics();
  return analytics
    .filter((item) => item.count > 1) // Only include searches used more than once
    .slice(0, limit)
    .map((item) => item.query);
};

/**
 * Get recent unique search queries
 */
export const getRecentSearches = (limit: number = 5): string[] => {
  const history = getSearchHistory();
  const uniqueQueries = new Set<string>();
  const recentQueries: string[] = [];

  for (const item of history) {
    if (!uniqueQueries.has(item.query) && recentQueries.length < limit) {
      uniqueQueries.add(item.query);
      recentQueries.push(item.query);
    }
  }

  return recentQueries;
};
